import React, { useState, useRef } from 'react';
import { Phone, Mail, MessageSquare, MapPin, Clock, Star, CheckCircle, Send, X, Globe, User } from 'lucide-react';
import { FaWhatsapp, FaWaze, FaFacebook, FaMailBulk } from 'react-icons/fa';
import { motion, useInView } from 'framer-motion';
// import Reviews from '../components/Reviews';
import LawReviewsWidget from '../components/LawReviewsWidget';
import ServicesSection from '../components/ServicesSection';
import HomepageEligibilitySection from '../components/HomepageEligibilitySection';

const BusinessCardPage = () => {
  const headerRef = useRef(null);
  const buttonsRef = useRef(null);
  const aboutRef = useRef(null);
  
  const isHeaderInView = useInView(headerRef, { once: true, amount: 0.3 });
  const isButtonsInView = useInView(buttonsRef, { once: true, amount: 0.3 });
  const isAboutInView = useInView(aboutRef, { once: true, amount: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const logoVariants = {
    hidden: { opacity: 0, scale: 0.8, y: -20 },
    show: { 
      opacity: 1, 
      scale: 1, 
      y: 0, 
      transition: { 
        duration: 0.8, 
        ease: "easeOut",
        delay: 0.3 
      } 
    },
  };

  return (
    <div className="min-h-screen bg-white relative" dir="rtl">
      {/* Background image section */}
      <div className="h-80 bg-gray-200 overflow-hidden relative">
        <img
          src="./articlesImages/articleImage1.jpg"
          alt="רקע משפטי"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Logo positioned half on image, half on white background */}
      <motion.div
        variants={logoVariants}
        initial="hidden"
        animate="show"
        className="absolute top-60 left-0 right-0 z-10"
        style={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <div className="w-48 h-48 rounded-full flex items-center justify-center shadow-2xl border-4 border-gray-200 overflow-hidden bg-white">
          <img
            src="/logoChen.jpg"
            alt="לוגו מיכלסון"
            className="w-44 h-44 object-cover rounded-full"
          />
        </div>
      </motion.div>

      {/* Content starts with padding to account for overlapping logo */}
      <div className="pt-36 pb-8">
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mb-8 px-6"
        >
          <h1 className="text-2xl font-light text-gray-800 mb-2">
            חן מיכלסון | עו"ד לחדלות פירעון והוצאה לפועל
          </h1>
        </motion.div>

        {/* Contact buttons grid */}
        <motion.div
          ref={buttonsRef}
          variants={containerVariants}
          initial="hidden"
          animate={isButtonsInView ? "show" : "hidden"}
          className="max-w-sm mx-auto px-6 mb-12 relative z-20"
        >
          <div className="grid grid-cols-3 gap-6">
            {/* Row 1 */}
            <motion.a
              variants={itemVariants}
              href="mailto:Chen@michelson-law.com"
              className="flex flex-col items-center p-4 bg-white rounded-2xl shadow-md border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-300 hover:scale-105"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mb-3 shadow-md">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm text-gray-700 font-medium text-center">מייל</span>
            </motion.a>

            <motion.a
              variants={itemVariants}
              href="https://wa.me/972559670867"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 bg-white rounded-2xl shadow-md border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-300 hover:scale-105"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-3 shadow-md">
                <FaWhatsapp className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm text-gray-700 font-medium text-center">וואטסאפ</span>
            </motion.a>

            <motion.a
              variants={itemVariants}
              href="tel:+972559670867"
              className="flex flex-col items-center p-4 bg-white rounded-2xl shadow-md border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-300 hover:scale-105"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-3 shadow-md">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm text-gray-700 font-medium text-center">טלפון</span>
            </motion.a>

            {/* Row 2 */}
            <motion.a
              variants={itemVariants}
              href="https://waze.com/ul?ll=31.794418,34.6410578&navigate=yes"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 bg-white rounded-2xl shadow-md border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-300 hover:scale-105"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mb-3 shadow-md">
                <FaWaze className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm text-gray-700 font-medium text-center">ווייז</span>
            </motion.a>

            <motion.a
              variants={itemVariants}
              href="https://michelson-law.com/"
              className="flex flex-col items-center p-4 bg-white rounded-2xl shadow-md border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-300 hover:scale-105"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-gray-600 to-gray-700 rounded-full flex items-center justify-center mb-3 shadow-md">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm text-gray-700 font-medium text-center">אתר</span>
            </motion.a>

            <motion.a
              variants={itemVariants}
              href="https://www.facebook.com/profile.php?id=61572965766927"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-4 bg-white rounded-2xl shadow-md border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-300 hover:scale-105"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mb-3 shadow-md">
                <FaFacebook className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm text-gray-700 font-medium text-center">פייסבוק</span>
            </motion.a>
          </div>
        </motion.div>

        {/* Lawyer photo and info */}
        <div className="bg-transparent py-16">
          <div className="max-w-2xl mx-auto px-6">
            <motion.div
              ref={aboutRef}
              initial={{ opacity: 0, y: 30 }}
              animate={isAboutInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-8"
            >
              <motion.img
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isAboutInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                src="./חן1.jpg"
                alt="עו״ד חן מיכלסון"
                className="w-48 h-56 object-cover mx-auto rounded-lg shadow-lg mb-6"
              />
              
              <motion.h2
                initial={{ y: 20, opacity: 0 }}
                animate={isAboutInView ? { y: 0, opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-3xl font-bold mb-8 text-gray-800"
              >
                קצת עלי...
              </motion.h2>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isAboutInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="space-y-6 text-gray-700 text-lg leading-relaxed"
              >
                <p>
                  עורכת הדין חן מיכלסון עוסקת בייצוג חייבים ובהובלת הליכי חדלות פירעון והוצאה לפועל, ומביאה עמה ניסיון עשיר בליווי משפטי מקיף ללקוחות פרטיים ולעסקים.
                </p>
                <p>
                  המשרד מתמקד במתן פתרונות משפטיים יצירתיים ויעילים – החל מהגשת הבקשה לפתיחת ההליך, טיפול בהסרת עיקולים והגבלות, ניהול מו״מ מול נושים ועד לקבלת צו הפטר ומחיקת חובות.
                </p>
                <p>
                  תפיסת העולם המקצועית של עו"ד מיכלסון נשענת על שילוב בין ידע משפטי מעמיק, ניסיון פרקטי רב שנים וגישה אנושית המעניקה ללקוח ליווי צמוד ושקוף לאורך כל הדרך.
                </p>
              </motion.div>
                
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isAboutInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="bg-blue-50 p-6 rounded-xl border-l-4 border-blue-500 mt-8 shadow-lg"
              >
                <p className="text-blue-800 font-medium text-xl leading-relaxed">
                  המשרד רואה בליווי ההליך לא רק מענה משפטי אלא גם הזדמנות אמיתית לשיקום כלכלי, יציאה לדרך חדשה והשגת יציבות וביטחון לטווח ארוך.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isAboutInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 1.0 }}
                className="text-gray-700 text-lg leading-relaxed mt-6"
              >
                <p>
                  לקוחות המשרד נהנים מייצוג מקצועי בלתי מתפשר, מסירות ואכפתיות, לצד יכולת אסטרטגית להביא לתוצאות מיטביות בזמן קצר – ולהחזיר לחייבים את השליטה בחייהם הכלכליים.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="py-8 bg-white">
            {/* <Reviews /> */}
            <LawReviewsWidget />
        </div>

        {/* Services Section */}
        <div className="py-8 bg-white">
            <ServicesSection />
        </div>
      </div>
    </div>
  );
};

export default BusinessCardPage;